import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import humanizeString from 'humanize-string';
import { debounce, get, map, pick } from 'lodash';
import moment from 'moment';
import { Alert, Col, Form, Row } from 'react-bootstrap-5';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';

import SanitizedHTML from 'react-sanitized-html';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTrashAlt, faSyncAlt } from '@fortawesome/pro-solid-svg-icons';

import { Button } from 'components/ui/button';
import CallListForm from 'bundles/tasks/index/call_list_form';

import ContactPeopleForm from 'components/contacts/people/form';
import ContactCompaniesForm from 'components/contacts/companies/form';
import TaskTagForm from './task_tag_form';
import ManagedInput from 'components/form/managed_input';
import { ProjectDatabaseMemberPropTypes } from 'components/project/prop_types';
import taskPropTypes from 'components/tasks/prop_types';
import ReminderDatePicker from './reminder_date_picker';

import { isPresent } from 'helpers/string_helpers';

import TaskFormErrors from './task_form_errors';
import AddAttachmentToProjectIcon from './add_attachment_to_project_icon';
import AssociatedModelSelectors from './associated_model_selectors';
import { searchUsers } from '../users/api';
import { fetchTaskAttachments } from './api';

export default class TaskForm extends React.Component {
  static propTypes = {
    assigneeOptions: PropTypes.arrayOf(ProjectDatabaseMemberPropTypes),
    availableDatabaseOptions: PropTypes.arrayOf(PropTypes.array),
    calendarImport: PropTypes.bool,
    callListMode: PropTypes.oneOf(['new', 'existing']),
    contacts: PropTypes.array,
    defaultAssociatedModels: PropTypes.array,
    defaultAssociatedModelType: PropTypes.string,
    defaultTaskAssignee: PropTypes.shape({
      id: PropTypes.number,
      label: PropTypes.string,
      name: PropTypes.string,
      value: PropTypes.number,
    }).isRequired,
    defaultTaskList: PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    }),
    errors: PropTypes.arrayOf(PropTypes.string),
    existingTask: taskPropTypes,
    hideAssociations: PropTypes.bool,
    hideByProjectSelect: PropTypes.bool,
    hideCallListSelect: PropTypes.bool,
    hideContactSelect: PropTypes.bool,
    hideProjectSelect: PropTypes.bool,
    hidePropertySelect: PropTypes.bool,
    hideTagSelect: PropTypes.bool,
    isNote: PropTypes.bool,
    logTask: PropTypes.bool,
    projectId: PropTypes.number,
    providedTitle: PropTypes.string,
    saveButtonLabel: PropTypes.string,
    selectedContactName: PropTypes.string,
    selectedContacts: PropTypes.string,
    onCancel: PropTypes.func.isRequired,
    onCreateContact: PropTypes.func,
    onDelete: PropTypes.func,
    onRemoveContact: PropTypes.func,
    onRemoveTaskListItem: PropTypes.func,
    onSave: PropTypes.func.isRequired
  };

  static defaultProps = {
    assigneeOptions: [],
    contacts: [],
    defaultAssociatedModels: [],
    hideCallListSelect: true,
    hideAssociations: false,
    hideByProjectSelect: false,
    hideContactSelect: false,
    hideTagSelect: false
  };

  constructor(props) {
    super(props);

    this.debouncedLoadAssigneeOptions = debounce(this.loadAssigneeOptions, 250);

    const {
      defaultAssociatedModels,
      defaultTaskAssignee,
      existingTask,
      hideAssociations,
      isNote,
      logTask,
      providedTitle
    } = props;

    if (existingTask) {
      const fieldsToMap = [
        'assigneeId',
        'body',
        'id',
        'calendarImport',
        'hasCrmEmail',
        'logged',
        'pinned',
        'taskType',
        'title',
        'reminderDifference'
      ];

      const associatedModels = existingTask.associatedModels.map((model) => {
        const defaultAssociation = defaultAssociatedModels.some((association) => {
          return association.associatedModelId === model.associatedModelId &&
            association.associatedModelType === model.associatedModelType;
        });
        return { ...model, default: defaultAssociation };
      });

      this.state = {
        ...pick(props.existingTask, fieldsToMap),
        associatedModels,
        attachments: [],
        dueDate: moment(existingTask.dueDate).format(),
        showCreateCompany: false,
        showCreatePerson: false,
        showCreateTaskList: false,
        showCreateTaskTag: false,
        taskListItems: existingTask.taskListItems || [],
        taskTaggedRecords: existingTask.taskTaggedRecords || []
      };
    } else {
      this.state = {
        assigneeId: defaultTaskAssignee.id || defaultTaskAssignee.value,
        associatedModels: defaultAssociatedModels || [],
        attachments: [],
        body: '',
        callListName: null,
        dueDate: moment().startOf('hour').format(),
        hasCrmEmail: false,
        logged: logTask || false,
        pinned: false,
        reminderDifference: 0,
        showCreateCompany: false,
        showCreatePerson: false,
        showCreateTaskList: false,
        showCreateTaskTag: false,
        taskListItems: [],
        taskTaggedRecords: [],
        taskType: isNote ? 'note' : 'call',
        title: providedTitle || ''
      };
    }

    this.state.hideAssociations = hideAssociations;
  }

  componentDidMount() {
    const { contacts } = this.props;
    this.handleDefaultTaskList();

    this.setState((prevState) => {
      const { associatedModels, hasCrmEmail, id } = prevState;
      const associatedModelsSet = new Set([...associatedModels, ...contacts]);
      const nextState = { associatedModels: [...associatedModelsSet] };

      if (hasCrmEmail) {
        nextState.attachments = 'fetching';

        fetchTaskAttachments(id).then(attachments => this.setState({ attachments }));
      }

      return nextState;
    });
  }

  matchAssociations = (model1, model2) => (
    model1 === model2 || (model1.originalId && model2.originalId && model1.originalId === model2.originalId)
  );
  assigneeOptions = () => this.props.assigneeOptions.map(u => ({ value: u.id, label: u.name }))
  getDisplayClass = () => classNames({ 'fade-lock': this.isCrmEmail() });
  getSaveButtonLabel = () => this.props.saveButtonLabel || `Save ${this.detailLabelNoun()}`;
  isCalendarImport = () => !!get(this.props, 'existingTask.calendarImport');
  isCompleted = () => !!get(this.props, 'existingTask.completedAt');
  isCrmEmail = () => !!get(this.props, 'existingTask.hasCrmEmail');
  isForCallList = () => !!this.props.callListMode;
  isNewCallList = () => this.props.callListMode === 'new';
  isNote = () => this.state.taskType === 'note';
  showReminderSelect = () => !(this.state.logged || this.isCrmEmail() || this.isCompleted());
  valid = () => {
    const { assigneeId, body, callListName, dueDate, title } = this.state;

    if (this.isNote()) {
      return isPresent(body);
    } else {
      let isValid = assigneeId && dueDate && isPresent(title);

      if (this.isNewCallList()) {
        isValid = isValid && isPresent(callListName);
      }

      return isValid;
    }
  }

  handleDefaultTaskList = () => {
    const { defaultTaskList } = this.props;
    if (defaultTaskList == null) return;

    const { taskListItems } = this.state;
    if (taskListItems.filter(tli => tli.taskListId === defaultTaskList.id).length > 0) return;

    const taskListItem = {
      name: defaultTaskList.name,
      taskListId: defaultTaskList.id
    };

    this.setState(prevState => ({ taskListItems: [...prevState.taskListItems, taskListItem] }));
  }

  handleReminderDifference = (reminderDifference) => {
    this.setState({ reminderDifference });
  }

  handleInputChange = (event) => {
    const { providedTitle, selectedContactName } = this.props;
    const { checked, name, type, value } = event.target;
    const newValue = type === 'checkbox' ? checked : value;

    this.setState((prevState) => {
      const newState = { [name]: newValue };

      if (name === 'taskType') {
        if (newValue !== 'call' && prevState.taskListItems.length > 0) {
          newState.taskListItems = [];
        }

        if (newValue === 'note') {
          newState.title = null;
        } else {
          newState.pinned = false;
        }

        if (providedTitle) {
          const newTitle = newValue === 'other' ? 'Other Task' : humanizeString(newValue);
          newState.title = `${newTitle} with ${selectedContactName ? selectedContactName : '[{Contact}]'}`;
        }
      }

      return newState;
    });
  }

  handleDueDateChange = (dateObj) => {
    this.setState(dateObj);
  }

  handleAssigneeChange = (event) => {
    this.setState({ assigneeId: event.value });
  }

  handleReminderChange = (event) => {
    this.setState({ reminderDifference: event.target.value });
  }

  handleCallListNameChange = (event) => {
    this.setState({ callListName: event.target.value });
  }

  handleNewAssociatedModel = (model) => {
    this.setState((prevState) => {
      return {
        associatedModels: [...prevState.associatedModels, model],
        showCreateCompany: false,
        showCreatePerson: false
      };
    });
  }

  hasProjectLeaseSpaces = (model1, model2) => {
    return model2.associatedModelType === 'Project' &&
    model1.associatedModelType === 'LeaseSpace' &&
    model1.relatedAssociatedModelId === model2.associatedModelId;
  }

  handleRemoveAssociatedModel = (model) => {
    this.setState((prevState) => {
      const associatedModels = map(prevState.associatedModels, m => (
        this.matchAssociations(m, model) || this.hasProjectLeaseSpaces(m, model)
          ? { ...model, _destroy: true }
          : m
      ));
      return { associatedModels: associatedModels };
    });
  }

  handleNewTaskListItem = (item) => {
    this.setState((prevState) => {
      let updateExisting = false;
      const taskListItems = [...prevState.taskListItems];

      for (let i = 0; i < taskListItems.length; i++) {
        if (taskListItems[i].taskListId === item.taskListId) {
          taskListItems[i]._destroy = false;
          updateExisting = true;
          break;
        }
      }

      return { taskListItems: updateExisting ? taskListItems : [...taskListItems, item] };
    });
  }

  handleRemoveTaskListItem = (item) => {
    const { onRemoveTaskListItem } = this.props;
    if (onRemoveTaskListItem) onRemoveTaskListItem(item);

    this.setState((prevState) => {
      const taskListItems = prevState.taskListItems.map(i => i === item ? { ...item, _destroy: true } : i);
      return { taskListItems: taskListItems };
    });
  }

  handleNewTaskTaggedRecord = (item) => {
    this.setState((prevState) => {
      const taskTaggedRecords = prevState.taskTaggedRecords;
      const existingRecord = taskTaggedRecords.find(record => record.taskTagId === item.taskTagId);
      if (existingRecord) existingRecord._destroy = false;

      return { taskTaggedRecords: existingRecord ? taskTaggedRecords : [...taskTaggedRecords, item] };
    });
  }

  handleRemoveTaskTaggedRecord = (item) => {
    this.setState((prevState) => {
      const taskTaggedRecords = prevState.taskTaggedRecords.map(i => i === item ? { ...item, _destroy: true } : i);
      return { taskTaggedRecords: taskTaggedRecords };
    });
  }

  handleSave = () => {
    const { attachments, hasCrmEmail, hideAssociations, ...state } = this.state;
    this.props.onSave(state);
  }

  handleDelete = () => {
    const { hideAssociations, ...state } = this.state;
    this.props.onDelete(state);
    this.props.onCancel();
  }

  showCreateCompanyForm = () => this.setState({ showCreateCompany: true })
  showCreatePersonForm = () => this.setState({ showCreatePerson: true })
  cancelCreateCompany = () => this.setState({ showCreateCompany: false })
  cancelCreatePerson = () => this.setState({ showCreatePerson: false })
  showCreateTaskListForm = () => this.setState({ showCreateTaskList: true })
  cancelCreateTaskListForm = () => this.setState({ showCreateTaskList: false })
  showCreateTaskTagForm = () => this.setState({ showCreateTaskTag: true })
  cancelCreateTaskTagForm = () => this.setState({ showCreateTaskTag: false })

  handleCreateTaskListItem = (taskListItem) => {
    if (taskListItem == null) {
      this.cancelCreateTaskListForm();
      return;
    }

    this.setState((prevState) => {
      const newItem = {
        taskListId: taskListItem.id,
        name: taskListItem.name
      };

      return {
        taskListItems: [...prevState.taskListItems, newItem],
        showCreateTaskList: false
      };
    });
  }

  handleCreateTaskTaggedRecord = (taskTag) => {
    if (taskTag) {
      const newItem = {
        taskTagId: taskTag.id,
        name: taskTag.name
      };

      this.handleNewTaskTaggedRecord(newItem);
    }

    this.cancelCreateTaskTagForm();
  }

  handleCreateContact = (contact) => {
    this.handleNewAssociatedModel({
      ...contact,
      name: contact.label
    });
  }

  showDeleteIcon = item => !item.default;

  loadAssigneeOptions = (inputValue, callback) => {
    searchUsers(inputValue).then(({ users }) => {
      const options = users.map(user => ({ value: user.id.toString(), label: `${user.name} (${user.email})` }));

      callback(options);
    });
  }

  detailLabelNoun = () => {
    if (this.isNote()) return 'Note';
    if (this.state.logged) return 'Activity';
    if (this.isForCallList()) return 'Call';
    return 'Task';
  };

  shouldRenderAssociatedModel = model => (
    !model._destroy && !(model.default && model.associatedModelType === 'Multiple')
  );

  isRenewalReminder = () => {
    return this.state.taskType === 'renewal_reminder';
  }

  render() {
    const {
      showCreateCompany,
      showCreatePerson,
      showCreateTaskList,
      showCreateTaskTag
    } = this.state;

    if (showCreatePerson) {
      return (
        <ContactPeopleForm
          availableDatabaseOptions={this.props.availableDatabaseOptions}
          onCancel={this.cancelCreatePerson}
          onSave={this.handleCreateContact}
        />
      );
    } else if (showCreateCompany) {
      return (
        <ContactCompaniesForm
          availableDatabaseOptions={this.props.availableDatabaseOptions}
          onCancel={this.cancelCreateCompany}
          onSave={this.handleCreateContact}
        />
      );
    } else if (showCreateTaskList) {
      return (
        <CallListForm onClose={this.handleCreateTaskListItem} />
      );
    } else if (showCreateTaskTag) {
      return (
        <TaskTagForm onCancel={this.cancelCreateTaskTagForm} onSave={this.handleCreateTaskTaggedRecord} />
      );
    } else {
      return (
        <div className="contacts-task-form form-vertical-field-container">{this.renderTaskForm()}</div>
      );
    }
  }

  renderCalendarImportInfoAlert() {
    return (
      <Alert variant="info" className="mb-4 d-flex flex-row">
        <div className="p-2">
          <i className="fas fa-exclamation-circle fa-2x" />
        </div>
        <div className="ms-3">
          You cannot edit this Meeting in Buildout as it was created in your Google Calendar.
          Please edit it in that calendar and it will automatically update in Buildout.
        </div>
      </Alert>
    );
  }

  renderTaskForm() {
    const detailsFormGroupClass = classNames({
      required: !this.isForCallList(),
      optional: this.isForCallList()
    });

    return (
      <>
        <TaskFormErrors errors={this.props.errors} />
        {this.isCalendarImport() ? this.renderCalendarImportInfoAlert() : ''}
        {this.renderCallListName()}
        {this.renderTaskOptions()}
        <input
          className="d-none"
          name="logged"
          readOnly
          value={this.state.logTask}
        />

        <div>
          <Row className="pt-2 mb-1">
            <Col className={detailsFormGroupClass} xs={6}>
              <Form.Label>
                {this.detailLabelNoun()} Details
                {this.isForCallList() &&
                  <span className="text-muted"><em> (optional)</em></span>
                }
              </Form.Label>
            </Col>

            {this.isNote() &&
              <Col className="me-4 d-inline-block text-end form-group" xs={6}>
                <label className="clear-required">
                  <input
                    checked={this.state.pinned}
                    className="align-top me-2"
                    name="pinned"
                    type="checkbox"
                    onChange={this.handleInputChange}
                  />
                  Pin Note
                </label>
              </Col>
            }
          </Row>

          {!this.isNote() && !this.isForCallList() &&
            <div className="required">
              <input
                className={`${this.getDisplayClass()} form-control w-100 mb-4 task-details`}
                disabled={this.isCalendarImport()}
                name="title"
                placeholder={`Enter ${this.state.logged ? 'activity' : 'task'} (required)...`}
                readOnly={this.isCrmEmail()}
                required="required"
                type="text"
                value={this.state.title}
                onChange={this.handleInputChange}
              />
            </div>
          }
          {this.renderTaskBody()}
        </div>
        
        {this.renderAttachmentsList()}
        {this.renderAssignee()}
        {this.renderAssociationsSection()}
        {this.renderButtonBar()}
      </>
    );
  }

  renderAssociationsSection() {
    if (!this.state.hideAssociations) {
      return (
        <>
          <div className="fw-bold">Associated With</div>
          {this.renderAssociatedModelSelectors()}
          <div className="pt-2">
            {this.renderAssociatedModels()}
            {this.renderCallList()}
            {this.renderTaskTag()}
          </div>
          <hr />
        </>
      );
    }
  }

  renderTaskOptions() {
    const { isNote } = this.props;
    const showTaskTypeSelect = !(this.isForCallList() || isNote);
    const showDateSelect = !isNote;
    const showReminderSelect = !(this.isCalendarImport() || !this.showReminderSelect());
    const columnCount = [showDateSelect, showReminderSelect, showTaskTypeSelect].filter(Boolean).length;

    if (columnCount > 0) {
      const columnSize = 12 / columnCount;
      return (
        <Row className={this.getDisplayClass()}>
          {showTaskTypeSelect && <Col xs={columnSize}>{this.renderTaskTypeSelect()}</Col>}
          {showDateSelect && <Col xs={columnSize}>{this.renderDateSelect()}</Col>}
          {showReminderSelect && <Col xs={columnSize}>{this.renderReminderSelect()}</Col>}
        </Row>
      );
    }
  }

  renderAssociatedModelSelectors() {
    const {
      hideByProjectSelect,
      hideCallListSelect,
      hideContactSelect,
      hideProjectSelect,
      hidePropertySelect,
      hideTagSelect
    } = this.props;

    return (
      <AssociatedModelSelectors
        allowByProjectSelect={!hideByProjectSelect}
        allowCallListSelect={!hideCallListSelect}
        allowContactSelect={!hideContactSelect}
        allowProjectSelect={!hideProjectSelect}
        allowPropertySelect={!hidePropertySelect}
        allowTagSelect={!hideTagSelect}
        associatedModels={this.state.associatedModels}
        taskListItems={this.state.taskListItems}
        taskTaggedRecords={this.state.taskTaggedRecords}
        taskType={this.state.taskType}
        onCreateCompany={this.showCreateCompanyForm}
        onCreatePerson={this.showCreatePersonForm}
        onCreateTaskListItem={this.showCreateTaskListForm}
        onCreateTaskTag={this.showCreateTaskTagForm}
        onModelAdded={this.handleNewAssociatedModel}
        onTaskListAdded={this.handleNewTaskListItem}
        onTaskTagAdded={this.handleNewTaskTaggedRecord}
      />
    );
  }

  bodyPlaceholderText = () => {
    if (this.isNote()) return 'Enter details (required)...';
    if (this.isForCallList()) {
      return 'Additional call details or description (i.e. "Prospecting call", "Follow up", etc.)';
    }

    return `Enter any additional ${this.state.logged ? 'activity' : 'task'} details...`;
  };

  renderTaskBody() {
    if (this.isCrmEmail() || this.isCalendarImport()) {
      return <SanitizedHTML className="fade-lock" html={this.state.body} />;
    }

    const formGroupClass = classNames('form-group', {
      required: this.isNote(),
      optional: !this.isNote()
    });

    return (
      <div className={formGroupClass}>
        <textarea
          className="task-details form-control text w-100"
          name="body"
          placeholder={this.bodyPlaceholderText()}
          value={this.state.body}
          onChange={this.handleInputChange}
        />
      </div>
    );
  }

  renderAttachmentsList() {
    const { attachments, hasCrmEmail, id } = this.state;

    if (!hasCrmEmail) return;

    if (attachments === 'fetching') {
      return <FontAwesomeIcon className="text-muted" icon={faSyncAlt} name="fetching" spin />;
    }

    if (attachments.length === 0) return;

    const { projectId } = this.props;

    return (
      <div className="mb-2">
        <div className="fw-bold">Attachments</div>
        {attachments.map(a => (
          <div key={a.id}>
            <a href={a.url} rel="noopener noreferrer" target="_blank">{a.name}</a>
            {projectId && <AddAttachmentToProjectIcon attachmentId={a.id} projectId={projectId} taskId={id} />}
          </div>
        ))}
      </div>
    );
  }

  renderAssignee() {
    if (this.isCalendarImport() || this.isNote()) return;

    const selectProps = {
      defaultValue: [{
        value: get(this.props, 'existingTask.assigneeId') || this.props.defaultTaskAssignee.id,
        label: get(this.props, 'existingTask.assignee') || this.props.defaultTaskAssignee.name
      }],
      name: 'assigneeId',
      onChange: this.handleAssigneeChange
    };

    return (
      <div className="mb-2">
        <div className="fw-bold mb-1">{this.state.logged ? 'Completed By' : 'Assigned To'}</div>
        {this.assigneeOptions().length > 0 ? (
          <Select options={this.assigneeOptions()} {...selectProps} />
        ) : (
          <AsyncSelect loadOptions={this.debouncedLoadAssigneeOptions} {...selectProps} />
        )}
      </div>
    );
  }

  renderAssociatedModels() {
    let modelsToRender = this.state.associatedModels.filter(m => this.shouldRenderAssociatedModel(m));

    const uniqModelsObject = modelsToRender.reduce((acc, model) => {
      const identif = [model.associatedModelType, model.originalId || model.associatedModelId].join('#');

      if (acc[identif] === undefined || model.associatedModelId === model.originalId) {
        acc[identif] = model;
      }

      return acc;
    }, {});

    modelsToRender = Object.values(uniqModelsObject);

    if (modelsToRender.length === 0) return;

    const tags = modelsToRender.map((model) => {
      let displayName = model.name;

      switch (model.associatedModelType) {
      case 'Contacts::Company':
      case 'Contacts::Person': {
        if (this.props.hideContactSelect) return null;
        break;
      }
      case 'Contacts::TaskList':
        displayName = `List: ${displayName}`;
        break;
      case 'Project':
        displayName = `Project: ${displayName}`;
        break;
      case 'LeaseSpace':
        displayName = `Space: ${displayName}`;
        break;
      case 'Research::Property': {
        if (this.props.hidePropertySelect) return null;
        break;
      }
      }

      return this.renderTag(
        model.associatedModelId,
        'associated-model',
        model,
        displayName,
        this.handleRemoveAssociatedModel
      );
    });

    return tags;
  }

  renderCallList() {
    if (this.props.hideCallListSelect || this.state.taskType != 'call') return;

    const taskListItems = this.state.taskListItems.filter(item => !item._destroy);
    if (taskListItems.length === 0) return;

    const tags = taskListItems.map((model) => {
      const displayName = `List: ${model.name}`;
      return this.renderTag(
        model.taskListId,
        'associated-model',
        model,
        displayName,
        this.handleRemoveTaskListItem
      );
    });

    return tags;
  }

  renderTaskTag() {
    const taskTaggedRecords = this.state.taskTaggedRecords.filter(item => !item._destroy);
    if (taskTaggedRecords.length === 0) return;

    return taskTaggedRecords.map(model => (
      this.renderTag(
        model.taskTagId,
        'associated-model',
        model,
        `Tag: ${model.name}`,
        this.handleRemoveTaskTaggedRecord
      )
    ));
  }

  renderCallListName() {
    if (!this.isNewCallList()) return;

    return (
      <Row>
        <Col xs={12}>
          <div className="form-group required">
            <label className="label-light">Call List Name</label>
            <div>
              <input
                className="form-control w-100"
                name="call-list-name"
                placeholder="Call List Name"
                required="required"
                type="text"
                value={this.state.callListName}
                onChange={this.handleCallListNameChange}
              />
            </div>
          </div>
        </Col>
      </Row>
    );
  }

  renderTag(key, name, item, displayName, handleRemove) {
    return (
      <span className="text-muted me-5" key={key} name={name}>
        {displayName}

        {this.showDeleteIcon(item) &&
          <FontAwesomeIcon
            className="ms-3 clickable"
            icon={faTimes}
            name={`remove-${name}`}
            size="sm"
            onClick={() => handleRemove(item)}
          />
        }
      </span>
    );
  }

  renderTaskTypeSelect() {
    const label = this.state.logged ? 'Activity Type' : 'Task Type';

    return (
      <Form.Group className="form-group">
        <Form.Label>{label}</Form.Label>
        <select
          className="form-select"
          disabled={this.isCrmEmail() || this.isCalendarImport()}
          name="taskType"
          value={this.state.taskType}
          onChange={this.handleInputChange}
        >
          {this.renderTaskTypeOptions()}
        </select>
      </Form.Group>
    );
  }

  renderTaskTypeOptions() {
    const { logTask } = this.props;

    if (this.state.taskType === 'renewal_reminder') {
      return <option value="renewal_reminder">Renewal Reminder</option>;
    }

    return (
      <>
        <option value="call">Call</option>
        <option value="email">Email</option>
        {(logTask || this.isCalendarImport()) && (
          <>
            <option value="meeting">Meeting</option>
            <option value="note">Note</option>
          </>
        )}
        <option value="other">Other</option>
        <option value="showing">Showing/Tour</option>
        <option value="critical_date">Critical Date</option>
      </>
    );
  }

  renderDateSelect() {
    return (
      <Form.Group className="form-group">
        <Form.Label>{this.state.logged ? 'Completion' : 'Due'} Date</Form.Label>
        <div>
          <ManagedInput
            className="form-control"
            disabled={this.isCrmEmail() || this.isCalendarImport() || this.isRenewalReminder()}
            name="dueDate"
            type="datetime"
            value={this.state.dueDate}
            onChange={this.handleDueDateChange}
          />
        </div>
      </Form.Group>
    );
  }

  renderReminderSelect() {
    let selectComponent;

    if (this.state.taskType === 'renewal_reminder') {
      selectComponent = (
        <ReminderDatePicker
          reminderDifference={this.state.reminderDifference}
          onChange={this.handleReminderDifference}
        />
      );
    } else {
      selectComponent = (
        <select
          className="form-select"
          name="reminderDifferenceSelect"
          value={this.state.reminderDifference}
          onChange={this.handleReminderChange}
        >
          <option value="0">No Reminder</option>
          <option value={moment.duration(1, 'hours').asSeconds()}>One Hour</option>
          <option value={moment.duration(1, 'days').asSeconds()}>One Day</option>
          <option value={moment.duration(1, 'weeks').asSeconds()}>One Week</option>
        </select>
      );
    }

    return (
      <Form.Group className="form-group">
        <Form.Label>Email Reminder</Form.Label>
        {selectComponent}
      </Form.Group>
    );
  }

  renderDeleteButton() {
    if (this.props.existingTask && this.props.onDelete) {
      return (
        <div
          className="btn d-inline-block me-1"
          name="remove-task"
          onClick={() => this.setState({ confirmDelete: true })}
        >
          <FontAwesomeIcon icon={faTrashAlt} />
          <span className="ms-3">Delete {this.detailLabelNoun()}</span>
        </div>
      );
    }
  }

  renderConfirmDelete() {
    return (
      <div className="text-end">
        <div className="d-inline-block me-5">
          Are you sure you want to delete this "{this.state.taskType}" task?
        </div>
        <Button
          className="me-4"
          label="Cancel"
          name="cancel-delete"
          variant="ghost"
          onClick={() => this.setState({ confirmDelete: false })}
        />
        <Button
          disabled={!this.valid()}
          label="Delete"
          name="delete"
          variant="destructive"
          onClick={this.handleDelete}
        />
      </div>
    );
  }

  renderButtonBar() {
    if (this.state.confirmDelete) return this.renderConfirmDelete();

    return (
      <div className="text-end">
        {this.isCalendarImport() ? '' : this.renderDeleteButton()}
        <Button className="me-4" label="Cancel" name="cancel" variant="ghost" onClick={this.props.onCancel} />
        <Button disabled={!this.valid()} label={this.getSaveButtonLabel()} name="save" onClick={this.handleSave} />
      </div>
    );
  }
}
